import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { CustomTheme } from "../../../../types/customTheme";
import { fetchHistoricalTimeline } from "../../../../services/insightApp/historicalAnalytics";
import { formatDuration, formatTime } from "./historicalUtils";

/**
 * INSIGHT-HIST-16 — one order's historical sequence.
 *
 * Events are shown in the order they happened, with the completed durations
 * and the final order record when there is one. Gaps stay gaps: an order with
 * no delivery event simply has no delivery row, and an order that never
 * completed has no final record.
 *
 * Both tables carry the same header band and row borders as the tables on the
 * page behind the modal, so it reads as part of the same screen.
 */

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      backgroundColor: theme.palette.background.entity_background,
    },
    title: {
      color: theme.palette.custom.orange.contrastText,
    },
    sectionTitle: {
      color: theme.palette.custom.orange.contrastText,
      marginBottom: "8px",
    },
    headerCell: {
      backgroundColor: theme.palette.background.table_header_background,
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
      whiteSpace: "nowrap",
    },
    cell: {
      color: theme.palette.custom.orange.contrastText,
      borderBottom: `1px solid ${theme.palette.background.entity_border}`,
    },
    label: {
      color: theme.palette.custom.orange.contrastText,
    },
  }),
);

export interface OrderTimelineModalProps {
  open: boolean;
  onClose: () => void;
  locationId: string;
  orderNo: string;
  businessDate: string;
}

const OrderTimelineModal: React.FunctionComponent<OrderTimelineModalProps> = ({
  open,
  onClose,
  locationId,
  orderNo,
  businessDate,
}) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!open || !orderNo || !businessDate) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError("");

      try {
        const res = await fetchHistoricalTimeline(locationId, {
          orderNo,
          businessDate,
        });

        if (!cancelled) {
          setData(res?.data?.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError("The order timeline could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, locationId, orderNo, businessDate]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography>
        <Typography variant="h6" className={classes.title}>
          Order {orderNo} — {businessDate}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !data || !data.hasData ? (
          <Typography variant="body2" color="textSecondary">
            No historical events were recorded for this order.
          </Typography>
        ) : (
          <>
            {data.order && (
              <Box mb={2}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Order
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      Received
                    </Typography>
                    <Typography variant="body2" className={classes.label}>
                      {formatTime(data.order.receivedAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      Completed
                    </Typography>
                    <Typography variant="body2" className={classes.label}>
                      {formatTime(data.order.completedAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      Mode
                    </Typography>
                    <Typography variant="body2" className={classes.label}>
                      {data.order.mode || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      Driver
                    </Typography>
                    <Typography variant="body2" className={classes.label}>
                      {data.order.driverId || "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <Divider />
                </Box>
              </Box>
            )}

            {data.metrics && data.metrics.length > 0 && (
              <Box mb={3}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Completed durations
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headerCell}>
                        Metric
                      </TableCell>
                      <TableCell className={classes.headerCell}>
                        Station
                      </TableCell>
                      <TableCell className={classes.headerCell} align="right">
                        Duration
                      </TableCell>
                      <TableCell className={classes.headerCell} align="right">
                        Target
                      </TableCell>
                      <TableCell className={classes.headerCell} align="right">
                        Result
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.metrics.map((metric: any) => (
                      <TableRow
                        key={`${metric.metricType}-${metric.stationId || ""}`}
                      >
                        <TableCell className={classes.cell}>
                          {metric.metricType}
                        </TableCell>
                        <TableCell className={classes.cell}>
                          {metric.stationId || "-"}
                        </TableCell>
                        <TableCell className={classes.cell} align="right">
                          {formatDuration(metric.durationMs)}
                        </TableCell>
                        <TableCell className={classes.cell} align="right">
                          {formatDuration(metric.targetMs)}
                        </TableCell>
                        <TableCell className={classes.cell} align="right">
                          {metric.withinTarget === null ||
                          metric.withinTarget === undefined ? (
                            <Chip size="small" label="No target" />
                          ) : metric.withinTarget ? (
                            <Chip size="small" label="Within target" />
                          ) : (
                            <Chip size="small" color="secondary" label="Late" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Events
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Time</TableCell>
                  <TableCell className={classes.headerCell}>Event</TableCell>
                  <TableCell className={classes.headerCell}>Station</TableCell>
                  <TableCell className={classes.headerCell}>Driver</TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    Device
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.events.map((event: any, index: number) => (
                  <TableRow key={`${event.eventType}-${event.eventAt}-${index}`}>
                    <TableCell className={classes.cell}>
                      {formatTime(event.eventAt)}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {event.eventType}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {event.stationId || "-"}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {event.driverId || "-"}
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      {event.deviceId || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <ButtonCommon onClick={onClose} size="medium">
          Cancel
        </ButtonCommon>
      </DialogActions>
    </Dialog>
  );
};

export default OrderTimelineModal;
